import React, { useLayoutEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { ReadingProgress } from '../components/ReadingProgress';
import { MobileBottomCTA } from '../components/MobileBottomCTA';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { useScrollAnimation } from '../utils/AnimationObserver';
import { Bank, ArrowLeft } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { SEO } from '../components/SEO';
const fadeUpVariant = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
function ProofImage({
  src,
  alt,
  caption
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return <div className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200" data-id="element-939">
      <img src={src} alt={alt} loading="lazy" decoding="async" className="w-full h-auto" data-id="element-940" />
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-941">
          {caption}
        </p>}
    </div>;
}
const processSteps = [{
  number: '01',
  title: 'Chain workflow system',
  desc: 'Built a content management system where every screen lived as a synced component. Content strategy and design could run in parallel without conflicts. Our product lead noted we moved 30% more efficiently through screen updates.'
}, {
  number: '02',
  title: 'Accessibility as definition of done',
  desc: 'Partnered with accessibility specialist Tracey Jackson. WCAG 2.1 annotations became part of the "Ready for Dev" handoff. Every screen reviewed before engineering touched it.'
}, {
  number: '03',
  title: 'Design system alignment',
  desc: 'Updated 75% of outdated components and shipped 20 new local ones, including a vertical nav bar later adopted by other teams.'
}, {
  number: '04',
  title: 'Reimagined user journey',
  desc: 'Mapped a collaborative workflow inside the application itself. Multiple stakeholders could now complete their assigned portions independently, with account owners reassigning roles on the fly.'
}];
export function CaseStudyUSBank({
  isOverlay = false
}: {
  isOverlay?: boolean;
}) {
  const navigate = useNavigate();
  const {
    viewPreference
  } = usePortfolio();
  useCaseStudyNavigation();
  useScrollAnimation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  return <>
      <SEO title="US Bank Commercial Card - UX Case Study" description="Cutting a 54-day corporate card onboarding to 20 days." keywords={['UX Design', 'Fintech', 'Case Study']} url="https://mateocohen.com/case-study/us-bank" type="article" image="https://uploadthingy.s3.us-west-1.amazonaws.com/cGTn1zWyBRLJq9z8XJ288f/usbthumb.png" data-id="element-942" />
      <ReadingProgress color="#001E79" data-id="element-943" />
      {!isOverlay && <CustomCursor data-id="element-944" />}

      <div className="flex min-h-screen bg-white text-slate-900" data-id="element-945">
        <div className="flex-1 min-w-0 flex flex-col relative pb-20 lg:pb-0" data-id="element-946">
          {!isOverlay && <CaseStudyHeader data-id="element-947" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-948" />}

          <main className="flex-1 w-full pb-32" data-id="element-949">
            {/* HEADER BLOCK */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="pt-32 sm:pt-40 pb-16 sm:pb-24 px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full" data-id="element-950">
              <button type="button" onClick={() => navigate(viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional')} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-900 hover:text-slate-500 transition-colors mb-10 group" style={{
              fontFamily: "'Ronzino', sans-serif"
            }} data-id="element-951">
                <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" data-id="element-952" />
                Back to home
              </button>

              <div className="mb-8" data-id="element-953">
                <Bank className="w-8 h-8 text-[#001E79]" weight="light" data-id="element-954" />
              </div>

              <h1 className="text-3xl sm:text-5xl md:text-6xl font-light text-[#001E79] leading-[1.1] tracking-tight mb-4" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-955">
                U.S. Bank Commercial Card
              </h1>

              <p className="text-lg sm:text-xl text-slate-500 font-light mb-8" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-956">
                Cutting a 54-day corporate card onboarding down to 20.
              </p>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl" style={{
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }} data-id="element-957">
                I joined U.S. Bank's Team Flash in 2022 to fix a brand-new
                digital onboarding experience that was bleeding time. The
                platform had shipped, but the process underneath it hadn't
                changed. Two years of focused redesign moved it from one of the
                slowest steps in the customer journey to one of the most
                efficient.
              </p>
            </motion.section>

            {/* HERO IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-16 sm:mb-24" data-id="element-958">
              <div className="bg-slate-100 rounded-2xl py-10 sm:py-14 md:py-16 px-6 sm:px-10 md:px-14 flex items-center justify-center" data-id="element-959">
                <img src="https://cdn.magicpatterns.com/uploads/qmk3kLu5tjWSuKq1Da1MRB/usbcasestudyhomepage.png" alt="U.S. Bank Commercial Card onboarding interface" className="w-full max-w-none object-contain drop-shadow-xl" data-id="element-960" />
              </div>
            </motion.section>

            {/* META STRIP */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-961">
              <div className="border-t border-b border-slate-200 py-10" data-id="element-962">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-12" data-id="element-963">
                  <div data-id="element-964">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#001E79] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-965">
                      Product
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-966">
                      U.S. Bank Commercial Card
                    </div>
                  </div>
                  <div data-id="element-967">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#001E79] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-968">
                      My role
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-969">
                      Lead UX Designer
                    </div>
                  </div>
                  <div data-id="element-970">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#001E79] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-971">
                      Timeline
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-972">
                      2022 to 2024
                    </div>
                  </div>
                  <div data-id="element-973">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#001E79] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-974">
                      Skills
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-975">
                      Product design, Research, Design systems, Accessibility
                    </div>
                  </div>
                </div>
                <div className="pt-10 border-t border-slate-200" data-id="element-976">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#001E79] mb-2" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-977">
                    Team
                  </div>
                  <div className="text-base font-medium text-slate-900" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-978">
                    Mateo Cohen (Lead UX), Team Flash, Tracey Jackson
                    (Accessibility), Content Strategy partner
                  </div>
                </div>
              </div>
            </motion.section>

            {/* QUOTE-OVERLAY IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-979">
              <div className="relative rounded-2xl min-h-[280px] sm:h-[480px] sm:aspect-auto overflow-hidden flex items-center justify-center p-6 sm:p-16" data-id="element-980">
                <div className="absolute inset-0 bg-slate-900" aria-hidden="true" data-id="element-981" />

                <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-982">
                  <p className="text-lg sm:text-3xl md:text-4xl font-light italic text-white leading-snug" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-983">
                    "Running the research myself was the surest way to keep the
                    end user at the center of every decision."
                  </p>
                  <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-6" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-984">
                    Project note
                  </div>
                </div>
              </div>
            </motion.section>

            {/* CONTENT SECTIONS */}
            <section className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full space-y-24 sm:space-y-32" data-id="element-985">
              {/* Problem */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-986">
                <div className="flex items-center gap-3 mb-4" data-id="element-987">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#001E79'
                }} data-id="element-988">
                    01
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#001E79'
                }} aria-hidden="true" data-id="element-989" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#001E79] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-990">
                  Problem: A 54-day onboarding that nobody owned
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-991">
                  The newly-launched digital platform was supposed to replace a
                  manual process. Instead it inherited the same problems in a
                  different shell. Applicants and internal processors were
                  bouncing between stakeholders with no clear ownership,
                  duplicating effort, and unable to track progress.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-992">
                  Underneath it: missing Figma files, dev and design out of
                  sync, accessibility gaps, and a perceived step count that made
                  the flow feel longer than it was.
                </p>

                <div className="bg-slate-100 rounded-lg p-6 sm:p-8 my-10 overflow-x-auto" data-id="element-993">
                  <div className="text-xl sm:text-2xl font-light text-[#001E79] sm:whitespace-nowrap" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-994">
                    54 days of bank handling time, per application
                  </div>
                </div>
              </motion.div>

              {/* Research */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-995">
                <div className="flex items-center gap-3 mb-4" data-id="element-996">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#001E79'
                }} data-id="element-997">
                    02
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#001E79'
                }} aria-hidden="true" data-id="element-998" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#001E79] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-999">
                  Research: I ran every session myself
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1000">
                  We had a research team on the engagement, but access to the
                  right customers was tightly controlled and the cadence didn't
                  match how fast design was moving. Rather than wait, I
                  partnered with Jenny, our content designer, to source and run
                  our own moderated sessions in parallel, timed to each new
                  feature being ready for review. This was honestly the hardest
                  part of the project: an ongoing negotiation over who got to
                  talk to customers. The compromise we landed on was
                  hand-selected participants, vetted by the broader research
                  org, so we could move quickly without losing rigor.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1001">
                  Bringing the team along with that data was as important as
                  collecting it. Every round surfaced friction we hadn't
                  anticipated, from confusing labels to the way the progress
                  indicator shaped expectations. Jenny and I owned the synthesis
                  together: clustering findings, mapping them to screens, and
                  feeding them straight into the next sprint, so the whole team
                  could ship with confidence the design had already been
                  validated by customers.
                </p>
              </motion.div>

              {/* Process Timeline */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-1002">
                <div className="flex items-center gap-3 mb-4" data-id="element-1003">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#001E79'
                }} data-id="element-1004">
                    03
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#001E79'
                }} aria-hidden="true" data-id="element-1005" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#001E79] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-1006">
                  Process: Four moves that unlocked the team
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1007">
                  Two years of work, condensed into the four decisions that
                  actually moved the needle.
                </p>

                <div data-id="element-1008">
                  {processSteps.map((step, idx) => <div key={step.number} className="relative flex gap-5 sm:gap-8 pb-12 sm:pb-16 last:pb-0" data-id="element-1009">
                      {idx !== processSteps.length - 1 && <div className="absolute left-5 sm:left-6 top-10 sm:top-12 bottom-0 w-px bg-slate-200" aria-hidden="true" data-id="element-1010" />}
                      <div className="relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-slate-300 flex items-center justify-center flex-shrink-0" data-id="element-1011">
                        <span className="text-xs sm:text-sm font-medium text-slate-900" style={{
                      fontFamily: "'Ronzino', sans-serif"
                    }} data-id="element-1012">
                          {step.number}
                        </span>
                      </div>
                      <div className="flex-1 pt-1.5 sm:pt-2.5" data-id="element-1013">
                        <h4 className="text-lg sm:text-xl font-medium text-[#001E79] mb-2" style={{
                      fontFamily: "'Gefika', sans-serif"
                    }} data-id="element-1014">
                          {step.title}
                        </h4>
                        <p className="text-base text-slate-600 leading-relaxed max-w-2xl" style={{
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }} data-id="element-1015">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </motion.div>

              {/* Solution - Before/After */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-1016">
                <div className="flex items-center gap-3 mb-4" data-id="element-1017">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#001E79'
                }} data-id="element-1018">
                    04
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#001E79'
                }} aria-hidden="true" data-id="element-1019" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#001E79] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-1020">
                  Solution: Fewer perceived steps, more user control
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1021">
                  Users read the five-step progress bar as longer than the work
                  actually was. By folding the intro into the "Get Started"
                  flow, we dropped the visible step count from five to four
                  without removing a single screen. Same work. Different read.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-10" data-id="element-1022">
                  <ProofImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Old_design.png" alt="Old US Bank onboarding design" caption="Before" data-id="element-1023" />
                  <ProofImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/New_Design.png" alt="New US Bank onboarding design" caption="After" data-id="element-1024" />
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1025">
                  The most impactful change was the collaborators feature: a
                  small panel in the lower left that let multiple stakeholders
                  own their portions of an application independently. Account
                  owners could reassign roles on the fly, and users could skip
                  questions and return later. Two of our biggest pain points,
                  resolved in one move.
                </p>

                <ProofImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Collaborators.png" alt="Collaborators feature in the redesigned application" caption="Shipped: collaborators panel" data-id="element-1026" />
              </motion.div>

              {/* Leadership */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-1027">
                <div className="flex items-center gap-3 mb-4" data-id="element-1028">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#001E79'
                }} data-id="element-1029">
                    05
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#001E79'
                }} aria-hidden="true" data-id="element-1030" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#001E79] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-1031">
                  Leadership: Mentoring through the work
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1032">
                  Lead UX Designer was the title. In practice it meant owning
                  the design process, presenting sprint-by-sprint, and making
                  the case for user-driven decisions when business pressure
                  pulled the team another way.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1033">
                  The most rewarding part was mentoring our content strategist
                  into a designer. We worked through accessibility, design
                  system guidelines, responsive design in Figma, and how to
                  advocate for users when the business case was the loudest
                  voice in the room. By the time I left, she was leading her own
                  design conversations.
                </p>
              </motion.div>

              {/* Outcome */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-1034">
                <div className="flex items-center gap-3 mb-4" data-id="element-1035">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#001E79'
                }} data-id="element-1036">
                    06
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#001E79'
                }} aria-hidden="true" data-id="element-1037" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#001E79] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-1038">
                  Outcome: Shipped and measured
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 py-8 mb-8 border-y border-slate-100" data-id="element-1039">
                  <div data-id="element-1040">
                    <div className="text-4xl sm:text-5xl font-light text-[#001E79] mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-1041">
                      65%
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#001E79]" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-1042">
                      Reduction in handling time (54 to 20 days)
                    </div>
                  </div>
                  <div data-id="element-1043">
                    <div className="text-4xl sm:text-5xl font-light text-[#001E79] mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-1044">
                      98%
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#001E79]" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-1045">
                      Rated the flow easy or very easy
                    </div>
                  </div>
                  <div data-id="element-1046">
                    <div className="text-4xl sm:text-5xl font-light text-[#001E79] mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-1047">
                      96%
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#001E79]" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-1048">
                      Of CPS pipeline migrated to digital
                    </div>
                  </div>
                  <div data-id="element-1049">
                    <div className="text-4xl sm:text-5xl font-light text-[#001E79] mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-1050">
                      20k
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#001E79]" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-1051">
                      Annual hours of productivity unlocked
                    </div>
                  </div>
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-1052">
                  Targets were 90% satisfaction and a 50% time reduction. We
                  cleared both, plus shipped 17+ automated onboarding processes
                  through ServiceNow along the way.
                </p>
              </motion.div>
            </section>

            <CaseStudyNavigation previousCase={{
            category: 'UX/UI Redesign 2025',
            title: 'Faculty Information System',
            slug: 'case-western'
          }} nextCase={{
            category: 'Digital Protection',
            title: 'Cyber Security Platform',
            slug: 'cyber-security'
          }} data-id="element-1053" />
          </main>
        </div>
      </div>

      <FloatingButtons data-id="element-1054" />
      <ChatDrawer data-id="element-1055" />
      <MobileBottomCTA data-id="element-1056" />
      {!isOverlay && <Footer data-id="element-1057" />}
    </>;
}